import { inject, Injectable } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { LOCAL_STORAGE } from '../browser/storage.tokens';

/**
 * Service for managing feature flags via query parameters.
 *
 * Reads feature flags from URL (?featureflags=flag1,flag2) and stores them
 * in localStorage for persistent activation across sessions.
 *
 * Special flags:
 * - 'all': Activates all features
 * - 'none': Clears all feature flags
 *
 * @example
 * ```typescript
 * // In app initialization
 * featureFlagService.resolveQueryString();
 *
 * // Check if flag is active
 * if (featureFlagService.flagActivated('new-ui')) {
 *   // Show new UI
 * }
 * ```
 */
@Injectable({
  providedIn: 'root',
})
export class FeatureFlagService {
  readonly #route = inject(ActivatedRoute);
  readonly #storage = inject(LOCAL_STORAGE);

  /**
   * Resolves feature flags from query parameters and stores them.
   *
   * Call once during app initialization. Subscribes to query params and
   * persists flags to localStorage for session durability.
   */
  resolveQueryString(): void {
    this.#route.queryParams.subscribe((params: Params) => {
      const flagsString = params['featureflags'];
      if (!flagsString) {
        return
      }

      const flags: string[] = flagsString.split(',');
      if (flags.length === 0) {
        return;
      }

      this.storeFlags(flags);
    });
  }

  /**
   * Checks if a feature flag is currently activated.
   *
   * Returns true if the flag is in the active flags list or if 'all' flag is set.
   *
   * @param flag - Feature flag name to check
   * @returns True if flag is active, false otherwise
   */
  flagActivated(flag: string): boolean {
    const flags: string[] = this.getFlags();

    if (flags.find((x: string) => x === 'all') !== undefined) {
      return true;
    }

    return flags.find((x: string) => x === flag) !== undefined;
  }

  /**
   * Retrieves all currently active feature flags from localStorage.
   *
   * @returns Array of active flag names, empty array if no flags are set
   */
  getFlags(): string[] {
    const flags: string = this.#storage.getItem('featureflags') ?? '';

    if (flags === '') {
      return [];
    }

    return flags.split(',');
  }

  private storeFlags(flags: string[]): void {
    const noneKey = flags.find((x: string) => x === 'none');
    if (noneKey !== undefined) {
      this.#storage.removeItem('featureflags');
      return;
    }

    const allKey = flags.find((x: string) => x === 'all');
    if (allKey !== undefined) {
      this.#storage.setItem('featureflags', 'all');
      return;
    }

    this.#storage.setItem('featureflags', flags.join());
  }
}
